import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/_components/alert-dialog";
import { Badge } from "@/components/_components/badge";
import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/_components/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { Skeleton } from "@/components/_components/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_components/table";
import {
  CONFIDENCE_SCORES,
  QUESTION_TYPES,
  QUESTION_TYPE_OPTIONS,
  PAGINATION,
} from "@/constants/constants";
import { UI_MESSAGES } from "@/constants/ui-constants";
import { TRANS_MASTER_STATUS } from "@/enums/trans-master-status";
import { useGetTransMasterById, useUpdateTransMasterAction } from "@/lib/api/engageApi";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { useChatHistory } from "@/hooks/useChatHistory";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { cn, formatDateTime } from "@/lib/utils";
import { customInstance } from "@/lib/axios-instance";
import { API_CONFIG } from "@/constants/api";
import { env, envUtils } from "@/lib/env";
import { selectStyles } from "theme/components/select";
import { inputStyles } from "theme/components/input";
import ChatHistoryDialog from "@/components/chat-history-dialog";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { useAuthorities } from "@/hooks/useAuthorities";
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Download,
  Edit,
  ExternalLink,
  FileText,
  Hash,
  HelpCircle,
  History,
  Loader2,
  Mail,
  MessageSquare,
  Plus,
  RotateCcw,
  Save,
  Search,
  Settings,
  Trash2,
  Users,
  X,
  Eye,
  Info,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { textareaStyles } from "theme/components/textarea";
import * as React from "react";

interface Question {
  question: string;
  questionNo: number;
  questionType: keyof typeof QUESTION_TYPES;
  confidenceScore: number;
  maxFollowUpPerQuestion: number;
  orderNo?: number; // Optional since it's added during save
}

interface PrescreeningData {
  jobId: string;
  criteriaQuestions: {
    questions: Question[];
  };
  initialOutreachMessage: {
    im: string;
    email: {
      emailSubject: string,
      emailMessage: string;
    }
  }[];
}

interface Recipient {
  id: number;
  candidateId: string,
  recipientId: string;
  fullName: string;
  emailId: string;
  mobilePhoneNo: string;
  status: string;
  outcome?: string;
  outcomeDetails?: string;
  createdAt: string;
  lastResponeAt: string;
  updatedAt?: string;
}

interface RecipientsFilter {
  transId: string;
  email?: string;
  mobilePhoneNo?: string;
  fullName?: string;
  status?: string;
  outcome?: string;
}

interface SearchFormData {
  email: string;
  phone: string;
  fullName: string;
  status: string;
  outcome: string;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  ...Object.values(TRANS_MASTER_STATUS).map((status) => ({
    value: status,
    label: status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
  })),
];

const OUTCOME_OPTIONS = [
  { value: "all", label: "All Outcomes" },
  { value: "qualified", label: "Qualified" },
  { value: "disqualified", label: "Disqualified" },
  { value: "followUpRequired", label: "Follow Up Required" },
  { value: "pending", label: "Pending" },
  { value: "inProgress", label: "In Progress" },
];

// API function for fetching recipients
const fetchRecipients = async (filter: RecipientsFilter, options?: { headers?: Record<string, string> }) => {
  const response = await customInstance<Recipient[]>({
    url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER_RECIPIENTS}/transId/${filter.transId}`,
    method: 'POST',
    headers: options?.headers || {},
    data: {
      emailId: filter.email || undefined,
      mobilePhoneNo: filter.mobilePhoneNo || undefined,
      fullName: filter.fullName || undefined,
      status: filter.status || undefined,
      outcome: filter.outcome || undefined,
    }
  });
  return response;
};

const PrescreeningDetailsPage: React.FC = () => {
  return (
    <PageAccessGuard authority="CAMPAIGN_CANDIDATE_OUTREACH_VIEW">
      <PrescreeningDetailsContent />
    </PageAccessGuard>
  );
};

const PrescreeningDetailsContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { hasAuthority } = useAuthorities();
  const canUpdate = hasAuthority('CAMPAIGN_CANDIDATE_OUTREACH_UPDATE');
  const canTestingUpdate = hasAuthority('ADMIN_TESTING_UPDATE');
  const isProduction = envUtils.isProduction();
  const showCandidateActions = !isProduction && canTestingUpdate;
  const [data, setData] = useState<PrescreeningData | null>(null);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [originalQuestion, setOriginalQuestion] = useState<Question | null>(null);
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailMessage, setEmailMessage] = useState<string>("");
  const [imMessage, setImMessage] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);

  // Candidates search and pagination state
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    email: "",
    phone: "",
    fullName: "",
    status: "all",
    outcome: "all",
  });

  // Track initial load with a more stable approach
  const hasFetchedCandidates = useRef(false);

  // Dialog states for candidates
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRecipientDetails, setSelectedRecipientDetails] = useState<Record<string, any> | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isChatHistoryDialogOpen, setIsChatHistoryDialogOpen] = useState(false);
  const [chatHistoryData, setChatHistoryData] = useState<any[]>([]);
  // Chat history hook
  const { fetchChatHistory, clearChatHistory, isLoadingChatHistory, isClearingChat } = useChatHistory({
    onSuccess: (chatHistoryData, recipient) => {
      setChatHistoryData(chatHistoryData);
      if (recipient && 'id' in recipient) {
        setCurrentRecipientForChat(recipient as Recipient);
      }
      setIsChatHistoryDialogOpen(true);
    },
    onClearSuccess: () => {
      refetchCandidates();
    }
  });
  const [currentRecipientForChat, setCurrentRecipientForChat] = useState<Recipient | null>(null);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isEditContactDialogOpen, setIsEditContactDialogOpen] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<Recipient | null>(null);
  const [editContactForm, setEditContactForm] = useState({
    emailId: "",
    mobilePhoneNo: ""
  });
  const [isUpdatingContact, setIsUpdatingContact] = useState(false);

  // Helper function to validate and format phone number input
  const handlePhoneInputChange = (value: string) => {
    // Allow only + and 0-9 characters
    const cleanedValue = value.replace(/[^+0-9]/g, '');
    return cleanedValue;
  };

  // Use the API hook to fetch data
  const {
    data: transMasterData,
    isLoading,
    error: apiError,
    refetch
  } = useGetTransMasterById(Number(id) || 0, {
    query: {
      enabled: !!id && !Number.isNaN(Number(id)),
      retry: 1,
      refetchOnWindowFocus: false
    }
  });

  // Paginated query for candidates
  const paginatedQuery = usePaginatedQuery<RecipientsFilter, Recipient[]>(
    fetchRecipients,
    {
      pageSize: env.DEFAULT_PAGE_SIZE,
      onError: () => {
        // Reset the fetch flag on error to allow retry
        hasFetchedCandidates.current = false;
      }
    }
  );

  const {
    data: candidatesData,
    error: candidatesError,
    isPending: isCandidatesPending,
    executeQuery,
    currentPage,
    totalCount,
    setCurrentPage,
    currentFilter: candidatesFilter,
    refetch: refetchCandidates,
  } = paginatedQuery;

  const [selectedPageSize, setSelectedPageSize] = useState(env.DEFAULT_PAGE_SIZE);
  const pageSize = selectedPageSize;

  // Show toast error when API error occurs
  useEffect(() => {
    if (apiError) {
      handleError(apiError);
    }
  }, [apiError, handleError]);

  // Transform API data to component data structure
  useEffect(() => {
    const effectiveData = transMasterData;
    if (effectiveData?.data) {
      try {
        // Parse processDetails if it exists and contains JSON
        let parsedData: PrescreeningData | null = null;

        // Prioritize camelCase since backend now returns camelCase
        const processDetails = (effectiveData.data as any).processDetails;

        let rawParsedData: any = null;

        if (processDetails) {
          try {
            rawParsedData = JSON.parse(processDetails);

            // Transform the data to handle camelCase (prioritized) and snake_case (fallback)
            parsedData = {
              jobId: rawParsedData.jobId || (effectiveData.data as any).externalId || "",
              criteriaQuestions: {
                questions: (rawParsedData.criteriaQuestions?.questions || []).map((q: any, index: number) => {
                  const questionType = q.questionType || "MANDATORY";
                  // Use nullish coalescing for confidenceScore to allow 0 values
                  const confidenceScore = q.confidenceScore ?? q.confidenceScore ?? CONFIDENCE_SCORES[questionType as keyof typeof CONFIDENCE_SCORES] ?? CONFIDENCE_SCORES.MANDATORY;
                  return {
                    question: q.question || "",
                    questionNo: q.questionNo || index + 1,
                    questionType: questionType as keyof typeof QUESTION_TYPES,
                    confidenceScore: confidenceScore,
                    maxFollowUpPerQuestion: q.maxFollowUpPerQuestion || 1,
                    orderNo: q.orderNo || index + 1
                  };
                })
              },
              initialOutreachMessage: rawParsedData.initialOutreachMessage || [{ email: { emailMessage: "", emailSubject: "" }, im: "" }]
            };

          } catch {
            // Create default structure if parsing fails
            parsedData = {
              jobId: (effectiveData.data as any).externalId || "",
              criteriaQuestions: { questions: [] },
              initialOutreachMessage: [{ email: { emailMessage: "", emailSubject: "" }, im: "" }]
            };
          }
        } else {
          // Create default structure if no processDetails
          parsedData = {
            jobId: (effectiveData.data as any).externalId || "",
            criteriaQuestions: { questions: [] },
            initialOutreachMessage: [{ email: { emailSubject: "", emailMessage: "" }, im: "" }]
          };
        }

        if (parsedData) {
          setData(parsedData);

          // Set form fields from parsed data
          if (parsedData.initialOutreachMessage && parsedData.initialOutreachMessage.length > 0) {
            const outreachData = parsedData.initialOutreachMessage[0];
            setEmailSubject(outreachData.email?.emailSubject || "");
            setEmailMessage(outreachData.email?.emailMessage || "");
            setImMessage(outreachData.im || "");
          }


        }
      } catch {
        toast.error("Failed to process prescreening data");
      }
    }
  }, [transMasterData, apiError]);

  // Auto-reload data every 15 seconds when status is CREATED or APPROVED
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (transMasterData?.data?.status === TRANS_MASTER_STATUS.CREATED ||
      transMasterData?.data?.status === TRANS_MASTER_STATUS.APPROVED) {
      intervalId = setInterval(() => {
        refetch();
      }, 15000); // 15 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [transMasterData?.data?.status, refetch]);

  // Reset fetch flag when id changes
  useEffect(() => {
    hasFetchedCandidates.current = false;
  }, [id]);

  // Initial load for candidates - wait for both id and executeQuery to be available
  useEffect(() => {
    if (!id || !executeQuery || hasFetchedCandidates.current) {
      return;
    }

    hasFetchedCandidates.current = true;

    const filter: RecipientsFilter = {
      transId: id,
      email: undefined,
      mobilePhoneNo: undefined,
      fullName: undefined,
      status: undefined,
      outcome: undefined,
    };

    executeQuery(filter, 1);
  }, [id, executeQuery]);

  const handleCandidateSearch = useCallback((formData: SearchFormData) => {
    if (!id) {
      toast.error("Transaction ID is required");
      return;
    }

    const filter: RecipientsFilter = {
      transId: id,
      email: formData.email.trim() || undefined,
      mobilePhoneNo: formData.phone.trim() || undefined,
      fullName: formData.fullName.trim() || undefined,
      status: formData.status === "all" ? undefined : formData.status || undefined,
      outcome: formData.outcome === "all" ? undefined : formData.outcome || undefined,
    };

    executeQuery(filter, 1); // Reset to first page on new search
  }, [id, executeQuery]);

  const handleCandidateReset = () => {
    const emptyForm: SearchFormData = {
      email: "",
      phone: "",
      fullName: "",
      status: "all",
      outcome: "all",
    };
    setSearchForm(emptyForm);
    handleCandidateSearch(emptyForm);
  };

  const handleCandidateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleCandidateSearch(searchForm);
  };

  const fetchRecipientDetails = async (recipientId: number) => {
    setIsLoadingDetails(true);
    try {
      const response = await customInstance<Record<string, any>>({
        url: `${API_CONFIG.ENDPOINTS.TRANS_MASTER_RECIPIENTS}/outcome/id/${recipientId}`,
        method: 'GET'
      });
      setSelectedRecipientDetails(response.data);
      setIsDialogOpen(true);
    } catch (error: any) {
      console.error('Error fetching recipient details:', error);
      handleError(error, 'Failed to load recipient details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const fetchRecipientProfile = async (recipientId: number) => {
    setIsLoadingProfile(true);
    try {
      const response = await customInstance<any>({
        url: API_CONFIG.ENDPOINTS.TRANS_MASTER_RECIPIENTS + `/id/${recipientId}`,
        method: 'GET'
      });
      setProfileData(response.data);
      setIsProfileDialogOpen(true);
    } catch (error: any) {
      console.error('Error fetching recipient profile:', error);
      handleError(error, 'Failed to load recipient profile');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const openEditContactDialog = (recipient: Recipient) => {
    setEditingRecipient(recipient);
    setEditContactForm({
      emailId: recipient.emailId || "",
      mobilePhoneNo: recipient.mobilePhoneNo || ""
    });
    setIsEditContactDialogOpen(true);
  };

  const updateRecipientContact = async () => {
    if (!editingRecipient || !id) {
      toast.error("Missing required information");
      return;
    }

    // Validation
    if (!editContactForm.emailId.trim() && !editContactForm.mobilePhoneNo.trim()) {
      toast.error("Please provide at least one contact method (email or phone)");
      return;
    }

    // Email validation
    if (editContactForm.emailId.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editContactForm.emailId.trim())) {
        toast.error("Please enter a valid email address");
        return;
      }
    }

    setIsUpdatingContact(true);
    try {
      await customInstance<any>({
        url: API_CONFIG.ENDPOINTS.TRANS_MASTER_RECIPIENTS + '/update-contact',
        method: 'PUT',
        data: {
          transId: Number(id),
          recipientId: editingRecipient.id,
          emailId: editContactForm.emailId.trim() || null,
          mobilePhoneNo: editContactForm.mobilePhoneNo.trim() || null
        }
      });

      toast.success("Contact information updated successfully");
      setIsEditContactDialogOpen(false);

      // Reload the screen by refetching data
      refetchCandidates();

    } catch (error: any) {
      console.error('Error updating contact:', error);
      handleError(error);
    } finally {
      setIsUpdatingContact(false);
    }
  };

  const downloadRecipientsCSV = () => {
    if (!candidatesData || !Array.isArray(candidatesData) || candidatesData.length === 0) {
      toast.error("No candidates data to download");
      return;
    }

    // Prepare metadata
    const metadata = [
      ["# Candidate Export"],
      [`# Generated on: ${new Date().toISOString()}`],
      [`# Transaction ID: ${id}`],
      [`# Total Recipients: ${candidatesData.length}`],
      [`# Search Filters Applied: ${JSON.stringify(searchForm)}`],
      [""], // Empty row for separation
    ];

    // Prepare CSV headers
    const csvHeaders = [
      "Sl_No",
      "Reference_Number",
      "Candidate_ID",
      "Full_Name",
      "Email_ID",
      "Mobile_Phone_No",
      "Status",
      "Business_Outcome",
      "Outcome_Details",
      "Outcome_Details_Length",
      "Created At",
      "Last Responded At"
    ];

    // Prepare CSV data rows
    const csvRows = candidatesData.map((recipient, index) => {
      const createdAt = recipient.createdAt ? new Date(recipient.createdAt) : null;
      const last = recipient.lastResponeAt ? new Date(recipient.lastResponeAt) : null;

      return [
        (index + 1).toString(),
        `${recipient.id}`,
        recipient.candidateId || "",
        `"${(recipient.fullName || "").replace(/"/g, '""')}"`, // Escape quotes
        recipient.emailId || "",
        recipient.mobilePhoneNo || "",
        recipient.status || "",
        recipient.outcome || "",
        `"${(recipient.outcomeDetails || "").replace(/"/g, '""')}"`, // Escape quotes
        (recipient.outcomeDetails || "").length.toString(),
        createdAt ? createdAt.toISOString() : "",
        last ? last.toISOString() : ""
      ];
    });

    // Create CSV content with metadata and data
    const csvContent = [
      ...metadata.map(row => row.join(",")),
      csvHeaders.join(","),
      ...csvRows.map(row => row.join(","))
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `candidates-${id}-${timestamp}.csv`;

      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);

      toast.success(`Candidates list downloaded as ${filename}`);
    } else {
      toast.error("Download not supported in this browser");
    }
  };

  const getStatusBadge = (status: string) => {
    const normalizedStatus = status?.toUpperCase();

    switch (normalizedStatus) {
      case TRANS_MASTER_STATUS.CREATED:
        return <Badge variant="outline" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">Created</Badge>;
      case TRANS_MASTER_STATUS.GENERATED:
        return <Badge variant="default" className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">Generated</Badge>;
      case TRANS_MASTER_STATUS.APPROVED:
        return <Badge variant="default" className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">Approved</Badge>;
      case TRANS_MASTER_STATUS.OUTREACHED:
        return <Badge variant="default" className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">Outreached</Badge>;
      case TRANS_MASTER_STATUS.IN_PROGRESS:
        return <Badge variant="default" className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">In Progress</Badge>;
      case TRANS_MASTER_STATUS.CLOSED:
        return <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">Closed</Badge>;
      default:
        return <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">{status || 'Unknown'}</Badge>;
    }
  };

  const getOutcomeBadge = (outcome: string) => {
    switch (outcome?.toLowerCase()) {
      case 'qualified':
        return <Badge variant="default" className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">Qualified</Badge>;
      case 'disqualified':
        return <Badge variant="destructive" className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">Disqualified</Badge>;
      case 'followuprequired':
      case 'follow_up_required':
        return <Badge variant="outline" className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200">Follow Up Required</Badge>;
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">Pending</Badge>;
      case 'inprogress':
      case 'in_progress':
        return <Badge variant="default" className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">In Progress</Badge>;
      default:
        return <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200">{outcome || 'Unknown'}</Badge>;
    }
  };

  const handleQuestionTypeChange = (questionNo: number, newType: keyof typeof QUESTION_TYPES) => {
    if (!data) return;

    const updatedQuestions = data.criteriaQuestions.questions.map(q => {
      if (q.questionNo === questionNo) {
        return {
          ...q,
          questionType: newType,
          confidenceScore: CONFIDENCE_SCORES[newType]
        };
      }
      return q;
    });

    setData({
      ...data,
      criteriaQuestions: {
        questions: updatedQuestions
      }
    });


  };

  const handleConfidenceScoreChange = (questionNo: number, score: string) => {
    if (!data) return;

    // Allow empty string for user to clear the field (will be validated on save)
    if (score === "") {
      const updatedQuestions = data.criteriaQuestions.questions.map(q => {
        if (q.questionNo === questionNo) {
          return { ...q, confidenceScore: "" as any };
        }
        return q;
      });

      setData({
        ...data,
        criteriaQuestions: {
          questions: updatedQuestions
        }
      });
      return;
    }

    const numScore = Number.parseFloat(score);
    // Allow any input during typing, validation will happen on save
    if (!Number.isNaN(numScore)) {
      const updatedQuestions = data.criteriaQuestions.questions.map(q => {
        if (q.questionNo === questionNo) {
          return { ...q, confidenceScore: numScore };
        }
        return q;
      });

      setData({
        ...data,
        criteriaQuestions: {
          questions: updatedQuestions
        }
      });
    }
  };

  const handleQuestionChange = (questionNo: number, newQuestion: string) => {
    if (!data) return;

    const updatedQuestions = data.criteriaQuestions.questions.map(q => {
      if (q.questionNo === questionNo) {
        return { ...q, question: newQuestion };
      }
      return q;
    });

    setData({
      ...data,
      criteriaQuestions: {
        questions: updatedQuestions
      }
    });
  };

  const moveQuestion = (questionNo: number, direction: 'up' | 'down') => {
    if (!data) return;

    const questions = [...data.criteriaQuestions.questions];
    const currentIndex = questions.findIndex(q => q.questionNo === questionNo);

    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= questions.length) return;

    // Swap questions
    [questions[currentIndex], questions[newIndex]] = [questions[newIndex], questions[currentIndex]];

    // Renumber questions
    questions.forEach((q, index) => {
      q.questionNo = index + 1;
    });

    setData({
      ...data,
      criteriaQuestions: {
        questions
      }
    });
  };

  const deleteQuestion = (questionNo: number) => {
    if (!data) return;

    const updatedQuestions = data.criteriaQuestions.questions
      .filter(q => q.questionNo !== questionNo)
      .map((q, index) => ({ ...q, questionNo: index + 1 }));

    setData({
      ...data,
      criteriaQuestions: {
        questions: updatedQuestions
      }
    });



    toast.success(UI_MESSAGES.QUESTION_DELETED);
  };

  const handleMaxFollowUpChange = (questionNo: number, value: string) => {
    if (!data) return;

    // Allow empty string for user to clear the field (will be validated on save)
    if (value === "") {
      const updatedQuestions = data.criteriaQuestions.questions.map(q => {
        if (q.questionNo === questionNo) {
          return { ...q, maxFollowUpPerQuestion: "" as any };
        }
        return q;
      });

      setData({
        ...data,
        criteriaQuestions: {
          questions: updatedQuestions
        }
      });
      return;
    }

    const numValue = Number.parseInt(value);
    // Allow any input during typing, validation will happen on save
    if (!Number.isNaN(numValue)) {
      const updatedQuestions = data.criteriaQuestions.questions.map(q => {
        if (q.questionNo === questionNo) {
          return { ...q, maxFollowUpPerQuestion: numValue };
        }
        return q;
      });

      setData({
        ...data,
        criteriaQuestions: {
          questions: updatedQuestions
        }
      });
    }
  };

  const addNewQuestion = () => {
    if (!data) return;

    // Check if there's already an empty question being edited
    const hasEmptyQuestion = data.criteriaQuestions.questions.some(q => !q.question.trim());
    if (hasEmptyQuestion) {
      toast.error("Please complete the existing empty question before adding a new one");
      return;
    }

    // Check if there's already a question being edited
    if (editingRowId !== null) {
      toast.error("Please finish editing the current question before adding a new one");
      return;
    }

    const newQuestionNo = data.criteriaQuestions.questions.length + 1;
    const newQuestion: Question = {
      question: "",
      questionNo: newQuestionNo,
      questionType: "MANDATORY",
      confidenceScore: CONFIDENCE_SCORES.MANDATORY,
      maxFollowUpPerQuestion: 1
    };

    setData({
      ...data,
      criteriaQuestions: {
        questions: [...data.criteriaQuestions.questions, newQuestion]
      }
    });



    // Automatically set the new question to edit mode and store original
    setEditingRowId(newQuestionNo);
    setOriginalQuestion({ ...newQuestion });
    toast.success(UI_MESSAGES.NEW_QUESTION_ADDED);
  };

  const startEditing = (questionNo: number) => {
    if (!data) return;
    const question = data.criteriaQuestions.questions.find(q => q.questionNo === questionNo);
    if (question) {
      setOriginalQuestion({ ...question });
      setEditingRowId(questionNo);
    }
  };

  const cancelEditing = () => {
    if (!data || editingRowId === null || !originalQuestion) {
      setEditingRowId(null);
      setOriginalQuestion(null);
      return;
    }

    // Restore the original question values
    const updatedQuestions = data.criteriaQuestions.questions.map(q => {
      if (q.questionNo === editingRowId) {
        return { ...originalQuestion };
      }
      return q;
    });

    setData({
      ...data,
      criteriaQuestions: {
        questions: updatedQuestions
      }
    });

    setEditingRowId(null);
    setOriginalQuestion(null);
  };

  const saveEditing = () => {
    if (!data || editingRowId === null) {
      setEditingRowId(null);
      setOriginalQuestion(null);
      return;
    }

    // Find the question being edited
    const question = data.criteriaQuestions.questions.find(q => q.questionNo === editingRowId);

    if (!question) {
      setEditingRowId(null);
      setOriginalQuestion(null);
      return;
    }

    // Validate question text
    if (!question.question.trim()) {
      toast.error("Question text cannot be empty");
      return;
    }

    // Validate confidence score - must have a value
    const confidenceScore = typeof question.confidenceScore === 'string' ? Number.parseFloat(question.confidenceScore as string) : question.confidenceScore;
    if ((question.confidenceScore as any) === "" || question.confidenceScore === null || question.confidenceScore === undefined || Number.isNaN(confidenceScore)) {
      toast.error("Please enter a confidence score (0 to 1)");
      return;
    }
    if (confidenceScore < 0 || confidenceScore > 1) {
      toast.error("Confidence score must be between 0 and 1");
      return;
    }

    // Validate max follow-up - must have a value
    const maxFollowUp = typeof question.maxFollowUpPerQuestion === 'string' ? Number.parseInt(question.maxFollowUpPerQuestion as string) : question.maxFollowUpPerQuestion;
    if ((question.maxFollowUpPerQuestion as any) === "" || question.maxFollowUpPerQuestion === null || question.maxFollowUpPerQuestion === undefined || Number.isNaN(maxFollowUp)) {
      toast.error("Please enter max follow-up per question (minimum 1)");
      return;
    }
    if (maxFollowUp < 1) {
      toast.error("Max follow-up per question must be at least 1");
      return;
    }

    // If all validations pass, save the changes
    setEditingRowId(null);
    setOriginalQuestion(null);
    toast.success("Question updated successfully");
  };

  const { mutate: updateAction } = useUpdateTransMasterAction({
    mutation: {
      onSuccess: async () => {
        // Refetch the latest data from the backend
        await refetch();
        toast.success("Prescreening details updated successfully!");
        setIsSaving(false);
      },
      onError: (error) => {
        handleError(error);
        setIsSaving(false);
      },
    },
  });

  const handleSubmit = async () => {
    if (!data || !id || !effectiveTransMasterData?.data) return;

    setIsSaving(true);
    try {
      // Add orderNo to each question based on their position
      const questionsWithOrder = data.criteriaQuestions.questions.map((question, index) => ({
        ...question,
        orderNo: index + 1,
        questionNo: index + 1 // Also update questionNo to match order
      }));

      // Update the data with current form values and ordered questions
      const updatedData = {
        ...data,
        criteriaQuestions: {
          questions: questionsWithOrder
        },
        initialOutreachMessage: [
          {
            email: {
              emailSubject: emailSubject,
              emailMessage: emailMessage
            },
            im: imMessage
          }
        ]
      };

      // Use the API hook instead of direct fetch
      updateAction({
        id: Number(id),
        action: 'GENERATED',
        data: updatedData
      });
    } catch (error: any) {
      handleError(error);
      setIsSaving(false);
    }
  };

  const { mutate: approveAction } = useUpdateTransMasterAction({
    mutation: {
      onSuccess: async () => {
        // Refetch the latest data from the backend
        await refetch();
        toast.success("Prescreening details approved successfully!");
        setIsApproving(false);
      },
      onError: (error) => {
        handleError(error);
        setIsApproving(false);
      },
    },
  });

  const handleApprove = async () => {
    if (!data || !id || !effectiveTransMasterData?.data) return;

    setIsApproving(true);
    try {
      // Add orderNo to each question based on their position
      const questionsWithOrder = data.criteriaQuestions.questions.map((question, index) => ({
        ...question,
        orderNo: index + 1,
        questionNo: index + 1 // Also update questionNo to match order
      }));

      // Update the data with current form values and ordered questions
      const updatedData = {
        ...data,
        criteriaQuestions: {
          questions: questionsWithOrder
        },
        initialOutreachMessage: [
          {
            email: {
              emailSubject: emailSubject,
              emailMessage: emailMessage
            },
            im: imMessage
          }
        ]
      };

      // Use the API hook instead of direct fetch
      approveAction({
        id: Number(id),
        action: 'APPROVED',
        data: updatedData
      });
    } catch (error: any) {
      handleError(error);
      setIsApproving(false);
    }
  };

  // Use API data only
  const effectiveTransMasterData = transMasterData;

  // Check if the current status allows editing AND user has update authority
  const isEditable = canUpdate && (
    effectiveTransMasterData?.data?.status === TRANS_MASTER_STATUS.GENERATED ||
    effectiveTransMasterData?.data?.status === TRANS_MASTER_STATUS.OUTREACHED
  );

  // Helper function to extract jobNumber from addDetails JSON
  const getJobNumber = (addDetails: string | null | undefined) => {
    if (!addDetails) return null;
    try {
      const parsed = JSON.parse(addDetails);
      return parsed.jobNumber || null;
    } catch {
      return null;
    }
  };

  // Helper function to extract jobUrl from addDetails JSON
  const getJobUrl = (addDetails: string | null | undefined) => {
    if (!addDetails) return null;
    try {
      const parsed = JSON.parse(addDetails);
      return parsed.jobUrl || parsed.job_url || null;
    } catch {
      return null;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-background to-muted">
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Loader2 className="h-5 w-5 text-primary animate-spin" />
                </div>
                <div>
                  <div className="h-6 w-48 bg-muted rounded animate-pulse mb-1" />
                  <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="shadow-sm border-border bg-card/80 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">Loading campaign details...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (apiError && !effectiveTransMasterData) {
    return (
      <div className="bg-gradient-to-br from-background to-muted">
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-destructive/10 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">Error Loading Details</h1>
                  <p className="text-sm text-muted-foreground">Failed to load campaign data</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="shadow-sm border-destructive/30 bg-card/80 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Failed to load prescreening details</h3>
              <p className="text-muted-foreground text-sm mb-4">Error: {JSON.stringify(apiError)}</p>
              <Button onClick={() => window.location.reload()} variant="outline" className="text-destructive border-destructive hover:bg-destructive/10">
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!effectiveTransMasterData?.data) {
    return (
      <div className="bg-gradient-to-br from-background to-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="shadow-sm border-border bg-card/80 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No API data found</h3>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gradient-to-br from-background to-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="shadow-sm border-border bg-card/80 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No processed data found</h3>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-background to-muted">
      {/* Header Section */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/candidate-outreach')}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Campaigns
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">
                    Recruitment Details
                  </h1>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Badge
                variant={effectiveTransMasterData?.data?.status === TRANS_MASTER_STATUS.GENERATED ? "default" : "secondary"}
                className="px-3 py-1 shadow-sm"
              >
                {effectiveTransMasterData?.data?.status}
              </Badge>
              {showCandidateActions && (
                <Button
                  onClick={() => {
                    const webchatUrl = `/webchat?transId=${id}&agentType=CANDIDATE_OUTREACH`;
                    navigate(webchatUrl);
                  }}
                  disabled={effectiveTransMasterData?.data?.status !== TRANS_MASTER_STATUS.OUTREACHED}
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Web Chat
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Campaign Information Section */}
        <Card className="shadow-sm border-border bg-card/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <BarChart3 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  Campaign Information
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Basic campaign details and configuration
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <span className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Campaign
                </span>
                <div className="p-3 bg-muted rounded-lg border border-border">
                  <span className="text-foreground font-medium">
                    {(effectiveTransMasterData?.data as any)?.campaignName || "-"}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Campaign Code
                </span>
                <div className="p-3 bg-muted rounded-lg border border-border">
                  <span className="text-foreground font-medium">
                    {(effectiveTransMasterData?.data as any)?.campaignCode || "-"}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  Job Number
                </span>
                <div className="p-3 bg-muted rounded-lg border border-border">
                  {(() => {
                    const jobNumber = getJobNumber((effectiveTransMasterData?.data as any)?.addDetails);
                    const jobUrl = getJobUrl((effectiveTransMasterData?.data as any)?.addDetails);

                    if (jobUrl && jobNumber) {
                      return (
                        <a
                          href={jobUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5 hover:underline transition-colors font-medium"
                        >
                          {jobNumber}
                          <ExternalLink size={14} />
                        </a>
                      );
                    }

                    return (
                      <span className="text-foreground font-medium">
                        {jobNumber || "-"}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-foreground flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Status
                </span>
                <div className="p-3 bg-muted rounded-lg border border-border">
                  <Badge
                    variant={effectiveTransMasterData?.data?.status === TRANS_MASTER_STATUS.GENERATED ? "default" : "secondary"}
                    className="font-medium"
                  >
                    {effectiveTransMasterData?.data?.status || "-"}
                  </Badge>
                </div>
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Candidates
                </span>
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800 text-center">
                  <span className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {((effectiveTransMasterData?.data as any)?.recipientCount) || "0"}
                  </span>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Total Candidates</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Candidates Section */}
        <Card className="shadow-sm border-border bg-card/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    Candidates
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    View and search candidates
                  </p>
                </div>
              </div>
              {candidatesData && Array.isArray(candidatesData) && (
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    {totalCount} {totalCount === 1 ? 'candidate' : 'candidates'} found
                  </div>
                  {candidatesData.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadRecipientsCSV()}
                      className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 border-green-300 dark:border-green-700 hover:border-green-400 dark:hover:border-green-600 flex items-center gap-1"
                      title="Download candidates list as CSV"
                    >
                      <Download className="h-3 w-3" />
                      Export CSV
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Search Filter */}
            <Card>
              <CardHeader>
                <CardTitle>Search Candidates</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCandidateSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">Email ID</label>
                      <input
                        id="email"
                        type="email"
                        placeholder="Enter email"
                        value={searchForm.email}
                        onChange={(e) => setSearchForm(prev => ({ ...prev, email: e.target.value }))}
                        className={inputStyles}
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
                      <input
                        id="phone"
                        type="tel"
                        placeholder="Enter phone number"
                        value={searchForm.phone}
                        onChange={(e) => {
                          const cleanedValue = handlePhoneInputChange(e.target.value);
                          setSearchForm(prev => ({ ...prev, phone: cleanedValue }));
                        }}
                        className={inputStyles}
                      />
                    </div>

                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-1">Full Name</label>
                      <input
                        id="fullName"
                        placeholder="Enter full name"
                        value={searchForm.fullName}
                        onChange={(e) => setSearchForm(prev => ({ ...prev, fullName: e.target.value }))}
                        className={inputStyles}
                      />
                    </div>

                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-foreground mb-1">Status</label>
                      <select
                        id="status"
                        value={searchForm.status}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSearchForm(prev => ({ ...prev, status: e.target.value }))}
                        className={selectStyles}
                      >
                        <option value="">Select status</option>
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="outcome" className="block text-sm font-medium text-foreground mb-1">Business Outcome</label>
                      <select
                        id="outcome"
                        value={searchForm.outcome}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSearchForm(prev => ({ ...prev, outcome: e.target.value }))}
                        className={selectStyles}
                      >
                        <option value="">Select outcome</option>
                        {OUTCOME_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex gap-x-2 justify-end">
                    <Button
                      variant="outline"
                      type="button"
                      onClick={handleCandidateReset}
                      className="w-32"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                    <Button type="submit" className="w-32">
                      <Search className="h-4 w-4 mr-2" />
                      Search
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Results Section */}
            {isCandidatesPending && !candidatesData ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading candidates...</p>
              </div>
            ) : candidatesError && !candidatesData ? (
              <div className="text-center py-8 text-muted-foreground border border-destructive/30 rounded-lg p-8">
                <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Failed to load candidates</h3>
                <p className="text-muted-foreground text-sm mb-4">Error: {candidatesError.message}</p>
                <Button onClick={() => refetchCandidates()} variant="outline" className="text-destructive border-destructive hover:bg-destructive/10">
                  Try Again
                </Button>
              </div>
            ) : candidatesData && Array.isArray(candidatesData) && candidatesData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border border-border rounded-lg p-8">
                <Info className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Candidates Found</h3>
                <p className="text-muted-foreground mb-4">
                  We couldn't find any candidates matching your search criteria.
                </p>
                <Button
                  variant="outline"
                  onClick={handleCandidateReset}
                  className="text-primary border-primary hover:bg-primary/10"
                >
                  Clear Filters
                </Button>
              </div>
            ) : candidatesData && (
              <>
                {/* Candidates Table */}
                <div className="border border-border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-muted">
                      <TableRow className="border-border">
                        <TableHead className="font-semibold text-foreground">Sl No</TableHead>
                        <TableHead className="font-semibold text-foreground">Ref. No</TableHead>
                        <TableHead className="font-semibold text-foreground">Full Name</TableHead>
                        <TableHead className="font-semibold text-foreground">Candidate Id</TableHead>
                        <TableHead className="font-semibold text-foreground">Email ID</TableHead>
                        <TableHead className="font-semibold text-foreground">Mobile Phone No</TableHead>
                        <TableHead className="font-semibold text-foreground">Status</TableHead>
                        <TableHead className="font-semibold text-foreground">Business Outcome</TableHead>
                        <TableHead className="font-semibold text-foreground">Outcome Details</TableHead>
                        <TableHead className="font-semibold text-foreground">Chat History</TableHead>
                        {showCandidateActions && (
                          <TableHead className="font-semibold text-foreground">Actions</TableHead>
                        )}
                        <TableHead className="font-semibold text-foreground">Created At</TableHead>
                        <TableHead className="font-semibold text-foreground">Last Response At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {candidatesData?.map((recipient, index) => (
                        <TableRow key={recipient.recipientId} className="hover:bg-muted/50">
                          <TableCell className="font-medium text-foreground">
                            {(currentPage - 1) * pageSize + index + 1}
                          </TableCell>
                          <TableCell className="font-medium text-foreground">{recipient.id}</TableCell>
                          <TableCell className="font-medium text-foreground">{recipient.fullName}</TableCell>
                          <TableCell className="font-medium text-foreground">
                            {recipient.fullName ? (
                              <button
                                onClick={() => fetchRecipientProfile(recipient.id)}
                                disabled={isLoadingProfile}
                                className="text-primary hover:text-primary/80 hover:underline cursor-pointer font-medium"
                              >
                                {recipient.candidateId}
                              </button>
                            ) : (
                              recipient.candidateId
                            )}
                          </TableCell>
                          <TableCell className="text-foreground">{recipient.emailId || '-'}</TableCell>
                          <TableCell className="text-foreground">{recipient.mobilePhoneNo || '-'}</TableCell>
                          <TableCell>{getStatusBadge(recipient.status)}</TableCell>
                          <TableCell>{getOutcomeBadge(recipient.outcome || '')}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="max-w-xs truncate" title={recipient.outcomeDetails}>
                                {recipient.outcomeDetails || '-'}
                              </div>
                              {recipient.status === TRANS_MASTER_STATUS.CLOSED && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => fetchRecipientDetails(recipient.id)}
                                  disabled={isLoadingDetails}
                                  className="h-6 px-2 text-primary hover:text-primary/80 hover:bg-primary/10"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  View
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(recipient.status === TRANS_MASTER_STATUS.IN_PROGRESS || recipient.status === TRANS_MASTER_STATUS.CLOSED) && (
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => fetchChatHistory(recipient.id, recipient)}
                                  disabled={isLoadingChatHistory}
                                  className="h-8 w-8 p-0 text-primary hover:text-primary/80 hover:bg-primary/10"
                                  title="View Chat History"
                                >
                                  <History className="h-4 w-4" />
                                </Button>
                                {showCandidateActions && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                                        title="Clear Chat History"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Clear Chat History</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to clear the chat history for this recipient? This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => clearChatHistory('CANDIDATE_OUTREACH', recipient.id)}
                                          disabled={isClearingChat}
                                          className="bg-destructive hover:bg-destructive/90"
                                        >
                                          {isClearingChat ? "Clearing..." : "Clear History"}
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            )}
                          </TableCell>
                          {showCandidateActions && (
                            <TableCell>
                              {recipient.status !== TRANS_MASTER_STATUS.CLOSED && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditContactDialog(recipient)}
                                  className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title="Edit Contact Information"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-foreground">{formatDateTime(recipient.createdAt)}</TableCell>
                          <TableCell className="text-foreground">{formatDateTime(recipient.lastResponeAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination matching dashboard style */}
                <div className="flex items-center justify-between px-4 mt-4">
                  <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
                    {totalCount} {totalCount === 1 ? 'record' : 'records'}
                  </div>
                  <div className="flex w-full items-center gap-8 lg:w-fit">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Rows per page</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => {
                          const newSize = Number(value);
                          setSelectedPageSize(newSize);
                          if (candidatesFilter) {
                            executeQuery(candidatesFilter, 1, newSize);
                          } else {
                            setCurrentPage(1);
                          }
                        }}
                        disabled={isCandidatesPending}
                      >
                        <SelectTrigger className="h-8 w-[70px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                            <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        Page {currentPage} of {Math.ceil(totalCount / pageSize) || 1}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1 || isCandidatesPending}
                          title="First page"
                        >
                          <ChevronsLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1 || isCandidatesPending}
                          title="Previous page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage >= Math.ceil(totalCount / pageSize) || isCandidatesPending}
                          title="Next page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                          disabled={currentPage >= Math.ceil(totalCount / pageSize) || isCandidatesPending}
                          title="Last page"
                        >
                          <ChevronsRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Outreach Configuration Section */}
        <Card className="shadow-sm border-border bg-card/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Mail className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  Outreach Configuration
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Configure email and messaging templates
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-6">
                <div className="space-y-3">
                  <label htmlFor="email-subject" className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Subject
                    {!isEditable && <Badge variant="secondary" className="text-xs">Read Only</Badge>}
                  </label>
                  <input
                    id="email-subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject..."
                    disabled={!isEditable}
                    className="focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  />
                </div>
                <div className="space-y-3">
                  <label htmlFor="im-message" className="text-sm font-medium text-foreground flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    IM Outreach Message
                    {!isEditable && <Badge variant="secondary" className="text-xs">Read Only</Badge>}
                  </label>
                  <textarea
                    id="im-message"
                    value={imMessage}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setImMessage(e.target.value)}
                    className={cn(textareaStyles, "min-h-[148px]")}
                    placeholder="Enter IM outreach message..."
                    disabled={!isEditable}
                  />
                </div>
              </div>
              <div className="lg:col-span-2 space-y-6">
                <div className="space-y-3">
                  <label htmlFor="email-message" className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email Message
                    {!isEditable && <Badge variant="secondary" className="text-xs">Read Only</Badge>}
                  </label>
                  <textarea
                    id="email-message"
                    value={emailMessage}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailMessage(e.target.value)}
                    className={cn(textareaStyles, "min-h-[248px]")}
                    placeholder="Enter email message..."
                    disabled={!isEditable}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Questions Section */}
        <Card className="shadow-sm border-border bg-card/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-500/10 rounded-lg">
                  <HelpCircle className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    Screening Questions
                  </CardTitle>
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-primary rounded-full"></div>
                      <span className="text-sm text-muted-foreground">
                        Total: <span className="font-medium text-foreground">{data.criteriaQuestions.questions.length}</span>
                      </span>
                    </div>
                    {Object.values(QUESTION_TYPES).map((questionType, index) => {
                      const count = data.criteriaQuestions.questions.filter(q => q.questionType === questionType).length;
                      const colors = ['bg-red-500', 'bg-orange-500', 'bg-green-500', 'bg-purple-500'];
                      const colorClass = colors[index % colors.length];

                      return (
                        <div key={questionType} className="flex items-center space-x-2">
                          <div className={`w-3 h-3 ${colorClass} rounded-full`}></div>
                          <span className="text-sm text-muted-foreground">
                            {questionType}: <span className="font-medium text-foreground">{count}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              {isEditable && (
                <Button
                  onClick={addNewQuestion}
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {UI_MESSAGES.ADD_NEW_QUESTION}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {data.criteriaQuestions.questions.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border rounded-lg bg-muted/50">
                <HelpCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium text-foreground mb-2">No questions yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Start by adding your first screening question
                </p>
                {isEditable && (
                  <Button
                    onClick={addNewQuestion}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Question
                  </Button>
                )}
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader className="bg-muted">
                    <TableRow className="border-border">
                      <TableHead className="w-20 font-semibold text-foreground">Question No</TableHead>
                      <TableHead className="font-semibold text-foreground">Question</TableHead>
                      <TableHead className="w-32 font-semibold text-foreground">Question Type</TableHead>
                      <TableHead className="w-32 font-semibold text-foreground">Confidence Score</TableHead>
                      <TableHead className="w-32 font-semibold text-foreground">Max Followup Per Question</TableHead>
                      {isEditable && (
                        <TableHead className="w-32 font-semibold text-foreground">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.criteriaQuestions.questions.map((question, index) => (
                      <TableRow key={question.questionNo} className="hover:bg-muted/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-indigo-500/10 rounded-full flex items-center justify-center">
                              <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{question.questionNo}</span>
                            </div>
                            {isEditable && (
                              <div className="flex flex-col gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveQuestion(question.questionNo, 'up')}
                                  disabled={index === 0}
                                  className="h-6 w-6 p-0 hover:bg-muted"
                                >
                                  <ChevronUp className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveQuestion(question.questionNo, 'down')}
                                  disabled={index === data.criteriaQuestions.questions.length - 1}
                                  className="h-6 w-6 p-0 hover:bg-muted"
                                >
                                  <ChevronDown className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {editingRowId === question.questionNo && isEditable ? (
                            <textarea
                              value={question.question}
                              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleQuestionChange(question.questionNo, e.target.value)}
                              className={cn(textareaStyles, "min-h-[60px]")}
                              placeholder={UI_MESSAGES.ENTER_QUESTION_TEXT}
                            />
                          ) : (
                            <div className="text-sm text-foreground">
                              {question.question || (
                                <span className="text-muted-foreground italic">No question text</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingRowId === question.questionNo && isEditable ? (
                            <Select
                              value={question.questionType}
                              onValueChange={(value: keyof typeof QUESTION_TYPES) =>
                                handleQuestionTypeChange(question.questionNo, value)
                              }
                            >
                              <SelectTrigger className="focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {QUESTION_TYPE_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge
                              variant={question.questionType === "MANDATORY" ? "destructive" : "secondary"}
                              className="font-medium"
                            >
                              {question.questionType}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingRowId === question.questionNo && isEditable ? (
                            <div className="flex flex-col gap-1">
                              <input
                                type="number"
                                step="0.1"
                                value={question.confidenceScore === 0 ? "0" : (question.confidenceScore || "")}
                                onChange={(e) => handleConfidenceScoreChange(question.questionNo, e.target.value)}
                                placeholder="0.0 - 1.0 *"
                                className="w-24 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                required
                              />
                              <span className="text-xs text-red-500">* Required</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <div className="w-12 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-indigo-500 rounded-full transition-all duration-300"
                                  style={{ width: `${question.confidenceScore * 100}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium text-slate-700">{question.confidenceScore}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingRowId === question.questionNo && isEditable ? (
                            <div className="flex flex-col gap-1">
                              <input
                                type="number"
                                step="1"
                                value={question.maxFollowUpPerQuestion === 0 ? "" : (question.maxFollowUpPerQuestion || "")}
                                onChange={(e) => handleMaxFollowUpChange(question.questionNo, e.target.value)}
                                placeholder="Min: 1 *"
                                className="w-24 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                required
                              />
                              <span className="text-xs text-red-500">* Required</span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <span className="text-sm font-medium text-slate-700">{question.maxFollowUpPerQuestion}</span>
                            </div>
                          )}
                        </TableCell>
                        {isEditable && (
                          <TableCell>
                            <div className="flex gap-2">
                              {editingRowId === question.questionNo ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={saveEditing}
                                    className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    title="Save changes"
                                  >
                                    <Save className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={cancelEditing}
                                    className="h-8 w-8 p-0 text-slate-600 hover:text-slate-700 hover:bg-slate-50"
                                    title="Cancel editing"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => startEditing(question.questionNo)}
                                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    title="Edit question"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                        title="Delete question"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Question</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to delete this question? This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => deleteQuestion(question.questionNo)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons - Only show when status is GENERATED */}
        {isEditable && (
          <Card className="shadow-sm border-border bg-gradient-to-r from-muted/50 to-muted">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  onClick={handleSubmit}
                  size="lg"
                  disabled={isSaving || isApproving || isLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-200 min-w-[180px]"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Update Details
                    </>
                  )}
                </Button>
                {effectiveTransMasterData?.data?.status === TRANS_MASTER_STATUS.GENERATED && (
                  <Button
                    onClick={handleApprove}
                    size="lg"
                    disabled={isSaving || isApproving || isLoading || ((effectiveTransMasterData?.data as any)?.recipientCount || 0) < 1}
                    className="bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg transition-all duration-200 min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isApproving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve Details
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recipient Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recipient Details</DialogTitle>
            <DialogDescription>
              View detailed information about the selected recipient
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {isLoadingDetails ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : selectedRecipientDetails ? (
              <div className="space-y-3">
                {Object.entries(selectedRecipientDetails).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-4 py-2 border-b border-border">
                    <div className="font-medium text-foreground capitalize break-words">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </div>
                    <div className="col-span-2 text-foreground break-words overflow-wrap-anywhere">
                      {value !== null && value !== undefined ? (
                        typeof value === 'object' ? (
                          <pre className="text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap break-words">
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        ) : (
                          <span className="whitespace-pre-wrap">{String(value)}</span>
                        )
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No details available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Chat History Dialog */}
      <ChatHistoryDialog
        isOpen={isChatHistoryDialogOpen}
        onClose={() => setIsChatHistoryDialogOpen(false)}
        isLoading={isLoadingChatHistory}
        chatHistoryData={chatHistoryData}
        recipientData={currentRecipientForChat || undefined}
        transId={id || undefined}
        onDownloadCSV={undefined}
      />

      {/* Edit Contact Dialog */}
      <Dialog open={isEditContactDialogOpen} onOpenChange={setIsEditContactDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Contact Information</DialogTitle>
            <DialogDescription>
              Update the recipient's email address and phone number
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {editingRecipient && (
              <div className="bg-muted p-3 rounded-lg">
                <div className="text-sm">
                  <span className="font-medium text-foreground">Recipient:</span>
                  <span className="ml-2 text-foreground">{editingRecipient.fullName}</span>
                </div>
                <div className="text-sm mt-1">
                  <span className="font-medium text-foreground">Status:</span>
                  <span className="ml-2">{getStatusBadge(editingRecipient.status)}</span>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-foreground mb-1">
                  Email ID
                </label>
                <input
                  id="edit-email"
                  type="email"
                  placeholder="Enter email address"
                  value={editContactForm.emailId}
                  onChange={(e) => setEditContactForm(prev => ({ ...prev, emailId: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                />
              </div>

              <div>
                <label htmlFor="edit-phone" className="block text-sm font-medium text-foreground mb-1">
                  Mobile Phone Number
                </label>
                <input
                  id="edit-phone"
                  type="tel"
                  placeholder="Enter phone number"
                  value={editContactForm.mobilePhoneNo}
                  onChange={(e) => {
                    const cleanedValue = handlePhoneInputChange(e.target.value);
                    setEditContactForm(prev => ({ ...prev, mobilePhoneNo: cleanedValue }));
                  }}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                />
              </div>

              <div className="text-xs text-muted-foreground bg-primary/10 p-2 rounded">
                Note: At least one contact method (email or phone) is required.
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsEditContactDialogOpen(false)}
                disabled={isUpdatingContact}
              >
                Cancel
              </Button>
              <Button
                onClick={updateRecipientContact}
                disabled={isUpdatingContact}
                className="bg-green-600 hover:bg-green-700"
              >
                {isUpdatingContact ? "Updating..." : "Update Contact"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recipient Profile</DialogTitle>
            <DialogDescription>
              View comprehensive profile information for the selected recipient
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {isLoadingProfile ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ) : profileData ? (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-muted p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-foreground mb-3">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-foreground">ID:</span>
                      <span className="ml-2 text-foreground">{profileData.id || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Title:</span>
                      <span className="ml-2 text-foreground">{profileData.title || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Gender:</span>
                      <span className="ml-2 text-foreground">{profileData.gender || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Notice Period:</span>
                      <span className="ml-2 text-foreground">{profileData.noticePeriod || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Date Available:</span>
                      <span className="ml-2 text-foreground">{profileData.dateAvailable || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Job Stability Rating:</span>
                      <span className="ml-2 text-foreground">{profileData.jobStabilityRating || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Job Changed in 5 Years:</span>
                      <span className="ml-2 text-foreground">{profileData.jobChangedIn5Years || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-foreground">Onboarding Status:</span>
                      <span className="ml-2 text-foreground">{profileData.onBoardingStatus || '-'}</span>
                    </div>
                  </div>
                  {profileData.about && (
                    <div className="mt-4">
                      <span className="font-medium text-foreground">About:</span>
                      <p className="mt-1 text-foreground">{profileData.about}</p>
                    </div>
                  )}
                </div>

                {/* Additional profile sections would go here - truncated for brevity */}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No profile data available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default PrescreeningDetailsPage;
